import React, { useEffect, useState } from 'react';

import { ParsedData, StepProps, track } from '@betterment/js-runtime';
import { Text } from '@betterment/samba-react';
import { Box, Button, ErrorText, Heading } from '@betterment/style-closet-react';
import { isEqual as _isEqual, omit as _omit } from 'es-toolkit/compat';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';

import { AddressFormValues, CustomerSignupValues } from './Controller';

import { AddressForm, AddressSchema, buildRemoteAddressValidation } from '../Checking/address-helpers';
import { Address, AddressValidationParams, User } from '../Checking/data';
import { isManualReview, isVerified } from '../Checking/models/customer';
import FullPageLoader from '../Shared/components/FullPageLoader';
import { WorkflowLayout } from '../Shared/layout/WorkflowLayout';
import { locationAssign } from '../Shared/path-helpers';
import { urlBuilder } from '../Shared/urlBuilder';

const ConfirmFormSchema = Yup.object<Partial<{ residentialAddress: Address; mailingAddress: Address }>>().shape({
  residentialAddress: Yup.object().shape(AddressSchema),
  mailingAddress: Yup.object().shape(AddressSchema),
});

export interface Props extends StepProps<CustomerSignupValues> {
  user: User;
  validateAddress: (data?: AddressValidationParams) => Promise<ParsedData<{}>>;
  handleClose: () => void;
}

const ConfirmAddresses: React.FC<React.PropsWithChildren<Props>> = ({
  flowState,
  goToNextStep,
  goToPreviousStep,
  user,
  validateAddress,
  handleClose,
}) => {
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const validationSchema = ConfirmFormSchema.test(
    'is-remote-valid',
    'address is invalid',
    buildRemoteAddressValidation(validateAddress)
  );
  const initialValues: AddressFormValues = {
    residentialAddress: flowState.values.residentialAddress,
    mailingAddress: flowState.values.mailingAddress,
    fullName: user.fullName,
  };
  const { customer, stepTitle } = flowState.values;

  useEffect(() => {
    track('ElementViewed', { name: 'ConfirmAddresses', flow: 'OpenCheckingAccount' });
  }, []);

  if (customer && (isVerified(customer) || isManualReview(customer))) {
    locationAssign(urlBuilder.appHome.absolute());
    return <FullPageLoader />;
  }

  return (
    <WorkflowLayout
      feature="checking_customer_signup"
      pageTitle="Sign up - Checking"
      workflowTitle={stepTitle}
      onBack={goToPreviousStep}
      onClose={handleClose}
    >
      <Heading variant="primary">Confirm your residential and mailing addresses</Heading>
      <Text
        as="p"
        css={{
          textAlign: 'left',
          marginBottom: 'space.400',
        }}
      >
        Your mailing address is where we'll send your new Betterment Visa&reg; Debit Card.
      </Text>
      <Formik
        onSubmit={values => {
          track('ElementClicked', { name: 'ContinueCheckingSignup3' });
          goToNextStep(_omit(values, 'fullName'));
        }}
        initialValues={initialValues}
        validationSchema={validationSchema}
        validateOnBlur={false}
        validateOnChange={false}
      >
        {({ status, values }) => (
          <Form>
            <Box mb="9">
              <AddressForm
                titleText="Residential address"
                fieldNamePrefix="residentialAddress"
                onEdit={() => setIsEditingAddress(true)}
              >
                <AddressForm.FormattedAddress {...values.residentialAddress} />
              </AddressForm>
            </Box>
            <Box mb="9">
              <AddressForm
                titleText="Mailing address"
                fieldNamePrefix="mailingAddress"
                onEdit={() => setIsEditingAddress(true)}
              >
                {_isEqual(values.residentialAddress, values.mailingAddress) ? (
                  <AddressForm.Line>Same as residential address</AddressForm.Line>
                ) : (
                  <AddressForm.FormattedAddress {...values.mailingAddress} />
                )}
              </AddressForm>
            </Box>
            <Button type="submit" autoFocus={true}>
              {isEditingAddress ? 'Save and continue' : 'Continue'}
            </Button>
            {status && <ErrorText>{status}</ErrorText>}
          </Form>
        )}
      </Formik>
    </WorkflowLayout>
  );
};

export default ConfirmAddresses;
